/**
 * Response contracts, one per endpoint in collector/api/metrics.py.
 *
 * Field names mirror the server exactly — no client-side renaming, so a reader
 * can grep a field here and find it in the endpoint that produces it.
 */
import { z } from 'zod'

const generated = { generated_at: z.string() }

export const Queues = z.object({
  streams: z.array(
    z.object({
      stream: z.string(),
      depth: z.number(),
      /** claimed but not yet acked — stuck work */
      pending: z.number().nullable(),
      /** never read by any consumer — work the consumer is behind on */
      lag: z.number().nullable(),
      consumers: z.number().nullable(),
      /* Messages/second, from two samples of the monotonic `entries-read`.
       *
       * NULL IS NOT ZERO, and the UI must not conflate them. Null means the
       * server cannot state a rate yet — first sample, a consumer group
       * recreated (the counter resets on every deploy), or a sample older than
       * an hour. Zero means nothing moved, which is a real and actionable
       * answer. Rendering null as 0/s would paint a healthy stream as a dead
       * one, which is precisely what the flow board exists to make obvious. */
      rate_per_s: z.number().nullable().optional(),
    }),
  ),
  ...generated,
})
export type Queues = z.infer<typeof Queues>

export const Budget = z.object({
  sources: z.array(
    z.object({
      source: z.string(),
      used: z.number(),
      cap: z.number(),
      units: z.number(),
      throttled: z.number(),
      pct_used: z.number().nullable(),
      rate_per_s: z.number(),
      /** what today lands at if the current rate holds — the actionable figure */
      projected_eod: z.number(),
      projected_pct: z.number().nullable(),
    }),
  ),
  ...generated,
})
export type Budget = z.infer<typeof Budget>

const CoverageMetric = z.object({
  buckets: z.record(z.string(), z.number()),
  captured_at: z.string().nullable(),
  duration_ms: z.number().nullable(),
  /** how old the snapshot is; the UI must show this, never imply "live" */
  age_seconds: z.number().optional(),
  stale: z.boolean().optional(),
})
export const Coverage = z.object({
  metrics: z.record(z.string(), CoverageMetric),
  stale_after_seconds: z.number(),
  ...generated,
})
export type Coverage = z.infer<typeof Coverage>

export const Health = z.object({
  status: z.enum(['ok', 'degraded']),
  problems: z.array(z.string()),
  /* Age of the newest dead letter; null when the queue is empty or unreadable. */
  dead_letter_newest_age_s: z.number().nullable().optional(),
  roles: z.array(
    z.object({
      group: z.string(),
      stream: z.string(),
      consumers: z.number(),
      alive: z.number(),
    }),
  ),
  dead_letter: z.number(),
  throttled_today: z.number(),
  ...generated,
})
export type Health = z.infer<typeof Health>

export const Attribution = z.object({
  sources: z.array(
    z.object({ source: z.string(), requests: z.number(), pct: z.number() }),
  ),
  total: z.number(),
  ...generated,
})
export type Attribution = z.infer<typeof Attribution>

const EsIndex = z.union([
  z.object({
    index: z.string(),
    docs: z.number(),
    last_insert: z.string().nullable(),
    last_update: z.string().nullable(),
    age_seconds: z.number(),
    stale: z.boolean(),
  }),
  z.object({ index: z.string(), error: z.string() }),
])
export const EsHealth = z.union([
  z.object({ configured: z.literal(false) }),
  z.object({
    configured: z.literal(true),
    indices: z.record(z.string(), EsIndex),
    ...generated,
  }),
])
export type EsHealth = z.infer<typeof EsHealth>

const EsActivityIndex = z.union([
  z.object({
    inserted_total: z.number(),
    updated_total: z.number(),
    inserted_per_day: z.array(z.number()),
    updated_per_day: z.array(z.number()),
    days: z.array(z.string()),
  }),
  z.object({ error: z.string() }),
])
export const EsActivity = z.union([
  z.object({ configured: z.literal(false) }),
  z.object({
    start: z.string(),
    end: z.string(),
    indices: z.record(z.string(), EsActivityIndex),
    cached: z.boolean().optional(),
    cache_age_s: z.number().optional(),
  }),
])
export type EsActivity = z.infer<typeof EsActivity>

export const Divergence = z.union([
  z.object({ configured: z.literal(false) }),
  z.object({
    configured: z.literal(true),
    videos_90d: z.union([
      z.object({ error: z.string() }),
      z.object({
        elasticsearch: z.record(z.string(), z.number()),
        postgres: z.record(z.string(), z.number().nullable()).optional(),
        delta: z.record(z.string(), z.number()).optional(),
        /** false when the replica has drifted more than 1% of the denominator */
        agrees: z.boolean().optional(),
        es_query_ms: z.number(),
      }),
    ]),
    postgres_snapshot_age_s: z.number().nullable().optional(),
    ...generated,
  }),
])
export type Divergence = z.infer<typeof Divergence>

export const Jobs = z.object({
  jobs: z.array(
    z.object({
      job_id: z.string(),
      kind: z.string(),
      state: z.string(),
      total: z.number(),
      pending: z.number(),
      done: z.number().nullable(),
      skipped: z.number(),
      failed: z.number(),
      created_at: z.string(),
      finished_at: z.string().nullable(),
    }),
  ),
  ...generated,
})
export type Jobs = z.infer<typeof Jobs>

/* ---------------------------------------------------------------------------
 * Channel classification (mirrors-classifier)
 * ------------------------------------------------------------------------- */
const TermBucket = z.object({ key: z.union([z.string(), z.boolean()]), count: z.number() })

export const ClassifiedCounts = z.object({
  total: z.number(),
  last_1d: z.number(),
  last_7d: z.number(),
  last_30d: z.number(),
  _cached: z.boolean().optional(),
})
export type ClassifiedCounts = z.infer<typeof ClassifiedCounts>

export const ClassifierDistributions = z.object({
  total: z.number(),
  /* `country` arrives folded to one bucket per country. The index holds the
   * same country under several spellings -- 3,333,758 documents written by the
   * incumbent carry display names ('India' 567,629) while this pipeline writes
   * ISO codes ('IN') -- so the API merges them on read. Aggregating raw would
   * list India twice and understate both rows. Keys are alpha-2 codes where the
   * value could be resolved, and the original spelling where it could not. */
  countries: z.array(TermBucket),
  languages: z.array(TermBucket),
  tier_1: z.array(TermBucket),
  /* False until the classifier's migration 002 has added `tier_1.keyword` to
   * the index AND backfilled it. Aggregating that subfield before it exists is
   * a VALID query in which every document lands in the "missing" bucket, so the
   * API would otherwise report a confident `unclassified: 3,333,758` for
   * channels that all in fact have a tier_1. Optional, so a dashboard built
   * against an older API still parses. */
  tier_1_ready: z.boolean().optional(),
  channel_type: z.array(TermBucket),
  brand_safety: z.array(TermBucket),
  _cached: z.boolean().optional(),
})
export type ClassifierDistributions = z.infer<typeof ClassifierDistributions>

export const ClassifierDaily = z.object({
  days: z.array(z.string()),
  counts: z.array(z.number()),
  start: z.string(),
  end: z.string(),
  _cached: z.boolean().optional(),
})
export type ClassifierDaily = z.infer<typeof ClassifierDaily>

/* Video classification (vid-classification-gemma4, written from thanos).
 * Windows reuse ClassifiedCounts — the API returns the same shape on purpose. */
export const VideoDistributions = z.object({
  total: z.number(),
  tier_1: z.array(TermBucket),
  languages: z.array(TermBucket),
  brand_safety: z.array(TermBucket),
  /* true-bucket = classified WITHOUT images (frame fetch failed). A rising
   * rate is the thumbnail CDN path degrading while throughput looks fine. */
  text_only: z.array(TermBucket),
  _cached: z.boolean().optional(),
})
export type VideoDistributions = z.infer<typeof VideoDistributions>


/* ---------------------------------------------------------------------------
 * Pipeline topology — the flow board draws itself from this
 * ------------------------------------------------------------------------- */

/** producer → stream → worker → sink. `kind` decides how a node renders and
 *  which other endpoint supplies its figures. */
export const TopologyNode = z.object({
  id: z.string(),
  /* `upstream` is the classifier's addition: DeepSeek and the vLLM fleet are
   * STAGES on that board, not hidden infrastructure. The eleven-hour classify
   * stall was a DeepSeek daily cap, and with the upstream off the graph there
   * is nowhere for that to show. */
  kind: z.enum(['producer', 'stream', 'worker', 'sink', 'upstream']),
  label: z.string(),
  /* Workers only: the consumer group. This is the join key onto
   * /metrics/health (liveness) and /metrics/queues (lag, rate). Without it a
   * stage cannot show its own state. */
  group: z.string().optional(),
  /* Set only by the BOARD, never by the server: when a stream and the single
   * worker that drains it are merged into one lane, this carries the worker's
   * label so the lane can still name its role. See FlowBoard.mergeLanes. */
  laneOf: z.string().optional(),
  /* The scheduler only: which sweeps it runs. Sweeps share one node and tag
   * their edges instead of each becoming a node, which keeps the drawing
   * readable at four sweeps and still lets per-sweep reach be derived. */
  sweeps: z.array(z.string()).optional(),
  /* The classifier role this stage runs as (`--role <name>`), which is the key
   * its heartbeat uses. Distinct from `group`: the feeders have no consumer
   * group — they publish, they do not consume — but they do heartbeat, so role
   * is what makes a running feeder visible rather than blank. */
  role: z.string().optional(),
  /** vLLM only — fleet capacity. Static: this API cannot reach thor, so the
   *  board shows slots and deliberately no live rate. */
  slots: z.number().optional(),
})

export const Topology = z.object({
  nodes: z.array(TopologyNode),
  edges: z.array(z.object({
    from: z.string(),
    to: z.string(),
    /** set on scheduler edges — which sweep publishes this one */
    sweep: z.string().optional(),
  })),
  generated_at: z.string().optional(),
})
export type Topology = z.infer<typeof Topology>
export type TopologyNode = z.infer<typeof TopologyNode>


/** The classifier's queues. A SEPARATE Redis database from the collector's, so
 *  `configured: false` is a real state meaning no second client was wired —
 *  distinct from an empty pipeline, which would read as a dead one. */
export const ClassifierQueues = z.object({
  configured: z.boolean().optional(),
  streams: z.array(z.object({
    stream: z.string(),
    group: z.string(),
    lag: z.number().nullable(),
    pending: z.number().nullable(),
    consumers: z.number().nullable(),
    rate_per_s: z.number().nullable(),
  })),
  /** DeepSeek spend against its daily cap — the cause of the 11-hour stall. */
  llm: z.object({ used: z.number(), cap: z.number() }).nullable().optional(),
  /* Beating replicas per role, from the classifier's own heartbeat keys.
   * Zero is a real answer — it is what a wedged worker looks like, and the
   * 11-hour stall was exactly that. Null means the classifier Redis could not
   * be read at all, which is a different thing and must render differently. */
  workers: z.record(z.string(), z.number()).nullable().optional(),
  fleet_slots: z.number().optional(),
  generated_at: z.string().optional(),
})
export type ClassifierQueues = z.infer<typeof ClassifierQueues>

/** Live throughput per role, from each worker's own completed-work counter.
 *
 * NOT from Redis `entries-read`: trimming a capped stream rewrites that
 * counter, and on 2026-08-02 it read 213 keywords/s against a true 0.69/s --
 * a 300x error in the direction that renders a saturated lane as busy.
 *
 * `perS` NULL IS NOT ZERO, the same rule the Queues schema states. A rate needs
 * two samples, so the first press of Refresh after a deploy has no baseline and
 * the honest answer is "—". Zero means nothing moved, which is alarming and
 * actionable. Painting the first press red on every rollout teaches an operator
 * to ignore the tile.
 */
export const Throughput = z.object({
  roles: z.record(
    z.string(),
    z.object({
      /** cumulative work finished; distinguishes "new" from "dead" */
      total: z.number().nullable(),
      per_s: z.number().nullable(),
      per_min: z.number().nullable(),
    }),
  ),
  generated_at: z.string(),
})
export type Throughput = z.infer<typeof Throughput>

/**
 * The vidIQ pipeline's headline figures (SIL-10670).
 *
 * `stale_s` is the health signal, not `rows_written`, and the reason is the
 * failure this panel exists to catch: the pipeline this replaced ran for three
 * months writing NOTHING -- its last row landed 2026-04-21 while three
 * containers stayed up and reported healthy. A lifetime total goes on
 * rendering ~147M throughout and shows a dead pipeline as a large one, so the
 * count here is for scale only. Time-since-last-write is the figure that falls
 * over the moment the writing stops, and it is the one every state derives
 * from.
 */
export const VidiqCounts = z.object({
  /* Cumulative rows the worker has written, reported BY the worker through
   * Redis. Not a Postgres count: `scraped_at` is unindexed on a 147M-row
   * table, so counting there is a full scan that blew the API's timeout. */
  rows_written: z.number().nullable(),
  /* Seconds since a row landed — THE signal. The pipeline this replaced sat
   * three months without a write while containers, queues and row counts all
   * read healthy; this is the only figure that would have caught it. */
  stale_s: z.number().nullable(),
  last_written: z.string().nullable(),
  scope_min_subscribers: z.number(),
  /* What the staleness means: work outstanding on the bulk stream, and whether a
   * feeder is alive to queue more. Null/absent = could not be read. */
  queued: z.number().nullable().optional(),
  feeder_alive: z.boolean().nullable().optional(),
  api_resting: z.boolean().nullable().optional(),
  generated_at: z.string().optional(),
})
export type VidiqCounts = z.infer<typeof VidiqCounts>

/* Per-subscriber-tier vidIQ freshness, read from a SNAPSHOT (migration 016).
 *
 * The underlying scan needs the newest row per channel across 52M rows and takes
 * ~178s (measured prod 2026-08-06) against a 60s API timeout, so it is
 * materialised by the coverage role on a 6h cadence. `age_s` and `stale` come
 * from the server for exactly that reason: a snapshot rendered without its age
 * is indistinguishable from live data, and this is the panel whose whole purpose
 * is noticing when data has stopped moving. */
export const VidiqTier = z.object({
  /* '10M+' … 'under1K'. Server-sorted, richest first. */
  tier: z.string(),
  channels: z.number(),
  /* Channels whose newest row is within a few days of the tier's newest —
   * i.e. actually current, not merely present. `channels` minus this is the
   * tail that has stopped advancing. */
  channels_current: z.number(),
  /* THE LAST DATA POINT: newest stats_date held for the tier. Distinct from
   * last_scraped_at, because vidIQ returns its trailing 30 days and a dormant
   * channel legitimately ends days before today. */
  newest_stats_date: z.string().nullable(),
  oldest_stats_date: z.string().nullable(),
  last_scraped_at: z.string().nullable(),
  subscribers: z.number(),
  total_views: z.number(),
  earnings: z.number(),
})
export type VidiqTier = z.infer<typeof VidiqTier>

export const VidiqTiers = z.object({
  /* Null before the first capture — "not computed yet", which the panel must
   * say rather than rendering zeros that read as a dead pipeline. */
  captured_at: z.string().nullable(),
  age_s: z.number().nullable(),
  duration_ms: z.number().nullable().optional(),
  stale: z.boolean().optional(),
  stale_after_seconds: z.number().optional(),
  tiers: z.array(VidiqTier),
  generated_at: z.string().optional(),
})
export type VidiqTiers = z.infer<typeof VidiqTiers>

/* ---------------------------------------------------------------------------
 * Subscriber-tier activity — inserted/updated counts for videos AND channels,
 * broken down by the SAME T0–T5 subscriber bands VidiqTier already sorts by,
 * across four trailing windows (1/2/7/30d).
 *
 * A SNAPSHOT, THE SAME SHAPE OF ANSWER AS VidiqTiers, NOT LIKE PipelineHealth:
 * the underlying scan touches 442M video rows and 28M channel rows, so —
 * mirroring VidiqTiers exactly — it is computed on demand by an explicit
 * refresh and served from a cache the rest of the time. `captured_at` /
 * `age_s` / `duration_ms` / `stale` exist for the identical reason VidiqTiers's
 * do: a snapshot rendered without its own age is indistinguishable from live
 * data, and that is the one thing every manual-refresh panel on this board
 * must never let happen.
 *
 * BEFORE THE FIRST-EVER REFRESH (fresh deploy, nobody has clicked refresh
 * yet): `captured_at` / `age_s` / `duration_ms` are null and `activity` is
 * `{}` — no entity keys at all, not `video`/`channel` present with empty
 * tiers inside. The panel must read that as "never computed" — the same
 * state VidiqTiers already handles for its own `captured_at === null` case —
 * never as a table of zeros, and never as an error.
 * ------------------------------------------------------------------------- */
const TierActivityWindowCounts = z.object({
  inserted: z.number(),
  updated: z.number(),
})
export type TierActivityWindowCounts = z.infer<typeof TierActivityWindowCounts>

/* Window key is a STRING ('1' | '2' | '7' | '30'), the same window-keying
 * convention Embeddings' own `cost`/`throughput` use (see above) — server-side
 * JSON object keys are always strings, regardless of the numeric `windows`
 * array that states which ones actually exist this response. */
const TierActivityTierWindows = z.record(z.string(), TierActivityWindowCounts)

/* tier -> window -> counts, for one entity (video or channel). */
const TierActivityEntity = z.record(z.string(), TierActivityTierWindows)

/* Live state of an in-flight refresh, or null when nothing is running.
 *
 * `pct` IS NULLABLE ON PURPOSE and the UI must handle it. The refresh is two
 * long queries and a write, not N countable iterations, so there is no true
 * row-level progress; the bar is elapsed-vs-expected, anchored on how long the
 * PREVIOUS run took. On a first run there is no history, so there is no honest
 * estimate — the server sends null rather than a fabricated number, and the
 * panel shows an indeterminate bar with an elapsed counter instead. */
const TierActivityRefresh = z.object({
  in_progress: z.boolean(),
  phase: z.string(),
  phase_index: z.number(),
  phase_count: z.number(),
  elapsed_s: z.number(),
  expected_s: z.number().nullable(),
  pct: z.number().nullable(),
  /* True once the run passes its expected time. The bar HOLDS at 95% rather
   * than sitting at 100% while nothing happens, which is how progress bars
   * teach people to distrust them. */
  overrun: z.boolean(),
})
export type TierActivityRefresh = z.infer<typeof TierActivityRefresh>

export const TierActivity = z.object({
  captured_at: z.string().nullable(),
  age_s: z.number().nullable(),
  duration_ms: z.number().nullable(),
  windows: z.array(z.number()),
  tiers: z.array(z.string()),
  /* Keyed by entity ('video' | 'channel') -> tier -> window -> counts. `{}`
   * IS A VALID, MEANINGFUL VALUE — the pre-first-refresh state — not a
   * malformed payload, which is why this is a plain z.record rather than a
   * fixed `{ video, channel }` object that would require both keys to be
   * present even when nothing has ever been computed. */
  activity: z.record(z.string(), TierActivityEntity),
  stale_after_seconds: z.number(),
  stale: z.boolean(),
  generated_at: z.string(),
  /* Absent on older servers, so `.optional()` as well as nullable — the panel
   * must not blank out against a collector that predates this field. */
  refresh: TierActivityRefresh.nullable().optional(),
  /* Whether the VIDEO half is a sampled estimate. False since 2026-08-18 —
   * both halves are now exact counts. Kept (optional) rather than removed so
   * a dashboard deployed ahead of its collector can still tell the difference
   * and label it honestly; `video_sample_pct` is gone, and an older server
   * still sending it is stripped by zod rather than rejected. */
  video_estimated: z.boolean().optional(),
  /* POST /tier-activity/refresh only: true when this response launched a new
   * scan, false when the server served today's already-computed snapshot
   * instead (the same-day cache gate). Absent on every GET, and on a
   * collector that predates the gate — so `optional()`, and the panel must
   * treat absent as "no opinion", not as false. */
  recompute_started: z.boolean().optional(),
  /* GET only, and only when the LAST background scan failed. Before this
   * field existed a failed refresh was invisible everywhere but the
   * collector's own stderr: the POST had already answered 202, and a failure
   * deletes the lock/progress keys exactly as a success does, so the panel
   * quietly kept showing the previous snapshot with no hint anything had
   * gone wrong. Absent/undefined on every success and on a collector that
   * predates this field — `optional()`, no `.nullable()` needed since the
   * server omits rather than nulls it. */
  last_refresh_error: z.string().optional(),
})
export type TierActivity = z.infer<typeof TierActivity>

/* ---------------------------------------------------------------------------
 * Embeddings (DeepInfraMapper) -- vectorises video and channel documents for
 * semantic search. Served whole by ONE endpoint: coverage, cost across every
 * trailing window, the 30-day daily series and the three breakdowns all in
 * one payload, so one cached hook covers the whole tab the way /metrics/vidiq
 * covers its panel.
 * ------------------------------------------------------------------------- */

/** One document type's progress against its own total. `total` is the
 *  denominator for every percentage on this tab; done/failed/deferred/pending
 *  are each read as their own count against it rather than derived from one
 *  another, because the mapper may reclassify a document between them.
 *
 *  `pre_existing` / `embedded` (SIL-10670 coverage-counts-vectors fix) are the
 *  two fields ADDED here, both OPTIONAL for the same forward/backward-compat
 *  reason `throughput` is below: an older collector that has not deployed
 *  this fix yet must still parse, and the tab degrades to exactly the
 *  PRE-FIX arithmetic rather than crashing (see EmbeddingsCoverage's own
 *  `embedded ?? done` fallback in Embeddings.tsx).
 *
 *  WHY THEY EXIST. `channel_classification` carries ~3.29M documents a
 *  predecessor GPU pipeline embedded before this pipeline's `embed_state`
 *  bookkeeping ever existed -- they HAVE a vector and are genuinely done,
 *  but never got the `done` marker written. Reading `done` alone as "how
 *  much is embedded" (the tab's original behaviour) counted every one of
 *  those ~3.24M documents as outstanding work, reporting a COMPLETE
 *  channel pipeline as 13.1% done with 3,236,399 "pending" -- MEASURED live
 *  2026-08-08 (total 3,723,617; done 487,218; pending as then computed
 *  3,236,399; true state 100% embedded, 0 pending). `pre_existing` is that
 *  legacy, vectored-but-unmarked population; `embedded` is `done +
 *  pre_existing`, what "no more work needed" actually means, and is what
 *  the tab's headline percentage is now computed from instead of `done`.
 *  `done` itself keeps its exact prior meaning -- `embed_state === 'done'`
 *  -- unchanged. */
const EmbeddingsCoverageService = z.object({
  total: z.number(),
  done: z.number(),
  pre_existing: z.number().optional(),
  embedded: z.number().optional(),
  failed: z.number(),
  deferred: z.number(),
  pending: z.number(),
})
export type EmbeddingsCoverageService = z.infer<typeof EmbeddingsCoverageService>

/** Spend for one trailing window. `usd` is routinely sub-cent -- the whole
 *  reason format.ts grew a dedicated `usd()` formatter -- and `cache_hits`
 *  against `texts` is the mapper's entire economic argument: a text already
 *  embedded costs nothing to re-serve. */
const EmbeddingsCostWindow = z.object({
  tokens: z.number(),
  usd: z.number(),
  requests: z.number(),
  texts: z.number(),
  cache_hits: z.number(),
})
export type EmbeddingsCostWindow = z.infer<typeof EmbeddingsCostWindow>

/** The three average-rate fields, shared by the window-level entry below AND
 *  its `by_service` split -- the split carries exactly the same shape per
 *  service, not a subset. */
const EmbeddingsThroughputRates = z.object({
  avg_texts_per_s: z.number(),
  avg_docs_per_s: z.number(),
  avg_tokens_per_s: z.number(),
})

/** One trailing window's AVERAGE embedding rate -- never a live/instantaneous
 *  rate. `embedding_usage` (the same rows `EmbeddingsCostWindow` sums) is
 *  aggregated per calendar DAY on the server, not per second, so the only
 *  honest denominator collector/domain/embeddings.py had for this was the
 *  window's own wall-clock length (window days * 86,400 s) -- including
 *  every idle second in it. A mapper that embeds in short bursts and sits
 *  idle the rest of the time reports a MUCH lower number here than its true
 *  rate while running, easily two orders of magnitude off, which is why
 *  every field name is prefixed `avg_` rather than left as a bare `*_per_s`
 *  the way the collector tab's live-rate panels (Throughput.tsx, VidiqThroughput)
 *  use -- those measure two live samples of a monotonic counter; this does not,
 *  and must not be read the same way.
 *
 *  `by_service` (SIL-10670 follow-on) splits the same window's rates across
 *  the two document types -- the dashboard's actual ask was "both channel
 *  and video doc throughput", and the combined figure above can average a
 *  stalled channel mapper and a healthy video one into a number that looks
 *  fine. OPTIONAL, same forward/backward-compat reason `throughput` itself
 *  is on `Embeddings` below: an older collector that has not deployed this
 *  addition yet must still parse. Exactly `video`/`channel` -- the table's
 *  `probe`/`verify` testing rows are excluded server-side and never appear
 *  as a third key here. */
const EmbeddingsThroughputWindow = EmbeddingsThroughputRates.extend({
  by_service: z.object({
    video: EmbeddingsThroughputRates,
    channel: EmbeddingsThroughputRates,
  }).optional(),
})
export type EmbeddingsThroughputWindow = z.infer<typeof EmbeddingsThroughputWindow>
export type EmbeddingsThroughputRates = z.infer<typeof EmbeddingsThroughputRates>

/** One live-rate subject. A single field on purpose -- the pinned contract
 *  carries docs/s only (texts/s and tokens/s have no live source; they stay
 *  window averages on the tile).
 *
 *  `docs_per_s` NULL IS NOT ZERO -- the same rule Queues.rate_per_s and
 *  Throughput.per_s state, now measured server-side for this tab too. Null
 *  means the collector cannot state a live rate right now (measurement
 *  warming up -- e.g. no second counter sample yet after a deploy); 0.0 means
 *  it measured and genuinely nothing completed, which is a real, actionable
 *  answer and must render as a formatted zero, never a dash and never a bare
 *  `0`. */
const EmbeddingsLiveService = z.object({
  docs_per_s: z.number().nullable(),
})
export type EmbeddingsLiveService = z.infer<typeof EmbeddingsLiveService>

/** The collector's own SERVER-SIDE live docs/s (SIL-10670 live-throughput
 *  headline) -- the genuine right-now rate the `avg_*` windows above can
 *  never be. Two samples of the completed-documents counters, taken where
 *  the counters live, over a trailing span of `span_s` wall-clock seconds
 *  ending at `measured_at` -- the same two-samples-of-a-monotonic-counter
 *  measurement `Throughput` (the collector tab) reads per role, which is why
 *  this replaces the dashboard's old client-side between-two-refreshes
 *  approximation outright.
 *
 *  All three subjects and both span fields arrive together once `live` is
 *  present at all; each VALUE may still be null independently (unmeasurable
 *  right now), per EmbeddingsLiveService's own comment. */
const EmbeddingsLive = z.object({
  video: EmbeddingsLiveService,
  channel: EmbeddingsLiveService,
  combined: EmbeddingsLiveService,
  span_s: z.number().nullable(),
  measured_at: z.string().nullable(),
})
export type EmbeddingsLive = z.infer<typeof EmbeddingsLive>

const EmbeddingsDailyRow = z.object({
  day: z.string(),
  service: z.string(),
  tokens: z.number(),
  usd: z.number(),
  requests: z.number(),
  texts: z.number(),
  cache_hits: z.number(),
})
export type EmbeddingsDailyRow = z.infer<typeof EmbeddingsDailyRow>

/** One dimension value against its own total and its own done count, so
 *  partial coverage is visible per bucket rather than only volume — the same
 *  question EmbeddingsCoverageService asks once, asked again per slice. */
const EmbeddingsBreakdownRow = z.object({
  key: z.string(),
  total: z.number(),
  done: z.number(),
})
export type EmbeddingsBreakdownRow = z.infer<typeof EmbeddingsBreakdownRow>

/** DLQ census for `embedding_dead_letter` -- a Postgres table on the SAME RDS
 *  instance `embedding_usage` lives on. A document lands here only after
 *  mirrors-embedding has retried its Elasticsearch write 3 times and still
 *  failed, at which point it is marked `embed_state="failed"` so the pending
 *  query never re-picks it. That combination -- DeepInfra already BILLED for
 *  the embedding call, and the write is now permanently abandoned -- makes
 *  this a different and more serious fact than `coverage.*.failed` (which is
 *  still being retried and may yet succeed).
 *
 *  `video`/`channel` default to 0 server-side for a spec with no dead-lettered
 *  rows (see collector's EmbeddingDeadLetterCounts) -- a spec absent from the
 *  writing side's GROUP BY means 0, never null, so both are required numbers
 *  here rather than optional/nullable.
 *
 *  THE WHOLE FIELD IS OPTIONAL, same reason `throughput` is: this was added
 *  to the contract after the tab first shipped, so a collector that has not
 *  deployed it yet is an ordinary day-one state, not a malformed payload. */
const EmbeddingsDeadLetterCounts = z.object({
  video: z.number(),
  channel: z.number(),
  total: z.number(),
})
export type EmbeddingsDeadLetterCounts = z.infer<typeof EmbeddingsDeadLetterCounts>

/* ---------------------------------------------------------------------------
 * Pipeline health — per-worker-role DB query latency (p50/p99) and per-queue
 * consumer-lag status, added after a multi-day throughput collapse went
 * completely invisible: every queue/throughput panel on the collector tab
 * read normal because none of them ever asked "is the database itself slow
 * right now" — that only surfaced once someone happened to check the ES
 * dashboard. This endpoint is deliberately UNCACHED (no `cached`/
 * `generated_at` wrapper, unlike almost every other endpoint on this board)
 * so the panel reading it must poll rather than load-once — see the
 * `usePipelineHealth` hook's own comment in api/queries.ts for why that is a
 * deliberate departure from this board's normal "nothing polls" convention.
 * ------------------------------------------------------------------------- */

/** One worker role's DB query latency. A role with zero recorded samples is
 *  OMITTED from `db_latency_ms` entirely server-side, never present with
 *  null p50/p99 — so the schema does not need to encode "no data" for a role,
 *  only "no data for this SAMPLED role", which is the p50/p99-nullable case
 *  covered below. `p50`/`p99` are nullable rather than required numbers
 *  purely defensively: today's backend always sends numbers once
 *  `samples > 0`, but a future variant that records a sample count without a
 *  computable percentile (e.g. a histogram bucket that rolled over) must
 *  still parse rather than fail the whole panel over one role. */
const PipelineHealthDbLatencyRole = z.object({
  p50: z.number().nullable(),
  p99: z.number().nullable(),
  samples: z.number(),
})
export type PipelineHealthDbLatencyRole = z.infer<typeof PipelineHealthDbLatencyRole>

/** One queue's consumer-lag reading. `status` is computed server-side from
 *  `pct` (ok < 0.4, warning 0.4–0.7, critical >= 0.7) — the client renders
 *  whatever `status` the server sends and never recomputes the bucket
 *  itself, so a future change to the thresholds needs no dashboard release.
 *  `unknown` means the group or stream was not found at all, and is the one
 *  state where `lag`/`maxlen`/`pct` are ALL null together — a real reading
 *  of zero lag is a different, ordinary fact and must never be confused with
 *  this one (the exact "genuine 0 vs. absent" distinction this codebase's
 *  null-vs-zero discipline exists to protect, e.g. Queues.rate_per_s,
 *  Throughput.per_s, EmbeddingsLiveService.docs_per_s). */
const PipelineHealthQueueLagEntry = z.object({
  status: z.enum(['ok', 'warning', 'critical', 'unknown']),
  lag: z.number().nullable(),
  maxlen: z.number().nullable(),
  pct: z.number().nullable(),
})
export type PipelineHealthQueueLagEntry = z.infer<typeof PipelineHealthQueueLagEntry>

/** The six queue keys the endpoint ALWAYS reports, exactly — the collector's
 *  own Redis stream/consumer-group topology, fixed regardless of which roles
 *  happen to have recent DB samples. Named here once so the panel iterates
 *  the SAME fixed list rather than trusting whatever keys happen to arrive
 *  (which would let a missing key silently vanish from the table instead of
 *  rendering its own `unknown` row). */
export const PIPELINE_HEALTH_QUEUE_KEYS = [
  'channels.refresh', 'channels.discover', 'videos.refresh',
  'keywords.search', 'trending.regions', 'seeds',
] as const
export type PipelineHealthQueueKey = (typeof PIPELINE_HEALTH_QUEUE_KEYS)[number]

export const PipelineHealth = z.object({
  /* Keyed by worker role ('channel' | 'video' | 'discovery' | 'keyword' in
   * practice) — a `z.record`, not a fixed object, because a role with no
   * samples is omitted rather than present-with-nulls, so there is no fixed
   * set of keys to require the way `queue_lag`'s six streams are fixed. */
  db_latency_ms: z.record(z.string(), PipelineHealthDbLatencyRole),
  queue_lag: z.object({
    'channels.refresh': PipelineHealthQueueLagEntry,
    'channels.discover': PipelineHealthQueueLagEntry,
    'videos.refresh': PipelineHealthQueueLagEntry,
    'keywords.search': PipelineHealthQueueLagEntry,
    'trending.regions': PipelineHealthQueueLagEntry,
    seeds: PipelineHealthQueueLagEntry,
  }),
})
export type PipelineHealth = z.infer<typeof PipelineHealth>

export const Embeddings = z.object({
  coverage: z.object({
    video: EmbeddingsCoverageService,
    channel: EmbeddingsCoverageService,
  }),
  /* Optional for the same forward/backward-compat reason `throughput` and
   * `texts_per_document` are below -- see EmbeddingsDeadLetterCounts's own
   * comment for why this figure matters enough to earn its own top-level key
   * rather than folding into `coverage`. */
  dead_letter: EmbeddingsDeadLetterCounts.optional(),
  /* Keyed by trailing-window length in days, as a STRING key -- '1' | '2' |
   * '7' | '30' -- the same four windows the board's own RangeChips offer. All
   * four bands are computed server-side and served together; there is no
   * `?days=` on this endpoint, so the panel reuses the globally-selected
   * range only to decide which row to highlight, never to refetch. */
  cost: z.object({
    '1': EmbeddingsCostWindow,
    '2': EmbeddingsCostWindow,
    '7': EmbeddingsCostWindow,
    '30': EmbeddingsCostWindow,
  }),
  /* Same four windows as `cost`, same STRING keys, added after the tab
   * originally shipped -- OPTIONAL (unlike `cost`) so a board polling a
   * collector that has not deployed this addition yet still parses instead
   * of failing the whole panel over one late field. Mirrors
   * ClassifierDistributions.tier_1_ready, the board's existing precedent for
   * exactly this situation. */
  throughput: z.object({
    '1': EmbeddingsThroughputWindow,
    '2': EmbeddingsThroughputWindow,
    '7': EmbeddingsThroughputWindow,
    '30': EmbeddingsThroughputWindow,
  }).optional(),
  /* The server-side live rate (see EmbeddingsLive's own comment). OPTIONAL
   * as a WHOLE key: present on new collectors only, absent on older ones --
   * the same forward/backward-compat precedent as `by_service` and
   * `throughput` itself. The tab renders the pre-live presentation (window
   * averages primary) when this is absent, never a crash and never an
   * invented live figure. */
  live: EmbeddingsLive.optional(),
  /* Texts embedded per document (5 today -- see collector's TEXTS_PER_DOCUMENT).
   * Echoed from the server, same reason `model`/`price_per_million_tokens_usd`
   * are: so this tab's own prose never hardcodes a second copy that could
   * drift from the collector's if that constant ever changes. Optional for
   * the same forward/backward-compat reason `throughput` is. */
  texts_per_document: z.number().optional(),
  daily: z.array(EmbeddingsDailyRow),
  /* EMPTY MEANS "NOT AGGREGATABLE", NOT "NOTHING FOUND". The backend returns
   * `[]` for a dimension it cannot group by yet — the same shape a genuinely
   * empty result would have — so an empty array here must render as "not
   * available", never as an empty chart implying zero traffic. Compare
   * ClassifierDistributions.tier_1_ready, which states the same distinction
   * with an explicit flag; this endpoint has none, so emptiness IS the
   * signal. */
  breakdown: z.object({
    country: z.array(EmbeddingsBreakdownRow),
    category: z.array(EmbeddingsBreakdownRow),
    language: z.array(EmbeddingsBreakdownRow),
  }),
  model: z.string(),
  price_per_million_tokens_usd: z.number(),
  generated_at: z.string().optional(),
})
export type Embeddings = z.infer<typeof Embeddings>

/* ------------------------------------------------------------------ qdrant
 * Contracts for collector/api/qdrant_metrics.py — the QdrantMapper store.
 * Field names mirror the server exactly (grep a field here, find it there).
 * Every payload carries computed_at + cache_hit: the tab polls every 60 s and
 * must always be able to state its own age. */

const qdrantStamped = { computed_at: z.string(), cache_hit: z.boolean() }

export const QdrantOverview = z.object({
  gateway: z.object({
    search_capacity: z.number().nullable().optional(),
    search_in_flight: z.number().nullable().optional(),
    search_queue_depth: z.number().nullable().optional(),
    write_capacity: z.number().nullable().optional(),
    write_in_flight: z.number().nullable().optional(),
    write_queue_depth: z.number().nullable().optional(),
    breaker: z.string().nullable().optional(),
    qps_1m: z.number().nullable().optional(),
    search_qps_1m: z.number().nullable().optional(),
    write_qps_1m: z.number().nullable().optional(),
    age_s: z.number(),
  }).nullable(),
  sampler: z.object({
    cycle_ms: z.number().optional(),
    errors: z.number().optional(),
    age_s: z.number(),
  }).nullable(),
  raft: z.object({
    leader_present: z.boolean().nullable().optional(),
    pending_operations: z.number().nullable().optional(),
    peers: z.number().nullable().optional(),
    age_s: z.number(),
  }).nullable(),
  collections: z.array(z.object({
    name: z.string(),
    status: z.string().nullable().optional(),
    optimizer_ok: z.boolean().nullable().optional(),
    points: z.number().nullable().optional(),
    indexed_vectors: z.number().nullable().optional(),
    segments: z.number().nullable().optional(),
    shards_active: z.number().nullable().optional(),
    shards_dead: z.number().nullable().optional(),
    transfers: z.number().nullable().optional(),
    age_s: z.number(),
  })),
  nodes: z.array(z.object({
    meta: z.object({ node: z.string() }).passthrough(),
    cpu_busy_pct: z.number().nullable().optional(),
    mem_total_mb: z.number().nullable().optional(),
    mem_avail_mb: z.number().nullable().optional(),
    disk_avail_gb: z.number().nullable().optional(),
    load1: z.number().nullable().optional(),
  }).passthrough()),
  lanes: z.record(z.string(), z.object({
    qps_60s: z.number(),
    errors_60s: z.number(),
  })),
  state: z.enum(['ok', 'warn', 'crit']),
  note: z.string().nullable(),
  ...qdrantStamped,
})
export type QdrantOverview = z.infer<typeof QdrantOverview>

export const QdrantThroughput = z.object({
  window: z.string(),
  bucket_s: z.number(),
  series: z.array(z.object({
    t: z.string(),
    lane: z.string(),
    n: z.number(),
    qps: z.number(),
    errors: z.number(),
    sheds: z.number(),
    p95_ms: z.number().nullable(),
  })),
  ...qdrantStamped,
})
export type QdrantThroughput = z.infer<typeof QdrantThroughput>

const pct = z.object({
  p50: z.number().nullable(),
  p95: z.number().nullable(),
  p99: z.number().nullable(),
})
export const QdrantLatency = z.object({
  window: z.string(),
  lanes: z.array(z.object({
    lane: z.string(), n: z.number(),
    total: pct, cluster: pct, overhead: pct, queue: pct,
  })),
  ...qdrantStamped,
})
export type QdrantLatency = z.infer<typeof QdrantLatency>

export const QdrantStatus = z.object({
  window: z.string(),
  rows: z.array(z.object({
    lane: z.string(),
    class: z.string(),
    status: z.number(),
    n: z.number(),
  })),
  ...qdrantStamped,
})
export type QdrantStatus = z.infer<typeof QdrantStatus>

export const QdrantTopQueries = z.object({
  window: z.string(),
  rows: z.array(z.object({
    lane: z.string().nullable().optional(),
    endpoint: z.string().nullable().optional(),
    collection: z.string().nullable().optional(),
    client: z.string().nullable().optional(),
    k: z.number().nullable().optional(),
    n_fields: z.number().nullable().optional(),
    count: z.number(),
    done: z.number(),
    errors: z.number(),
    client_errors: z.number(),
    sheds: z.number(),
    qps: z.number(),
    avg_ms: z.number().nullable(),
    p50: z.number().nullable(),
    p95: z.number().nullable(),
    avg_cost: z.number().nullable(),
    last_seen: z.string(),
  })),
  filters: z.object({
    lane: z.string().nullable(),
    client: z.string().nullable(),
    collection: z.string().nullable(),
    endpoint: z.string().nullable(),
    status: z.string(),
  }),
  sort: z.string(),
  dir: z.string(),
  ...qdrantStamped,
})
export type QdrantTopQueries = z.infer<typeof QdrantTopQueries>

export const QdrantCluster = z.object({
  window: z.string(),
  node_series: z.array(z.object({
    node: z.string(),
    t: z.string(),
    cpu: z.number().nullable(),
    mem_avail_mb: z.number().nullable(),
    disk_avail_gb: z.number().nullable(),
    load1: z.number().nullable(),
  })),
  collection_series: z.array(z.object({
    collection: z.string(),
    t: z.string(),
    points: z.number().nullable(),
    shards_dead: z.number().nullable(),
  })),
  ...qdrantStamped,
})
export type QdrantCluster = z.infer<typeof QdrantCluster>

/** One caller's traffic against the gateway -- keyed by the mapper client key
 *  (an API key / service identity), not by lane or endpoint. `overall` on the
 *  parent object is this SAME row shape summed across every client, so a
 *  reader can compare one caller's slice against the whole without a second
 *  request.
 *
 *  Three distinct outcome counts, and they must never be folded together:
 *  `errors` (5xx) is the cluster/gateway failing the caller's request;
 *  `client_errors` (4xx) is the caller sending something the gateway
 *  rejected outright, its own fault; `sheds` (429) is backpressure working
 *  as designed, not a failure at all. Same three-way split QdrantTopQueries
 *  already carries per query signature, here per caller. */
const QdrantClientRow = z.object({
  client: z.string(),
  requests: z.number(),
  qps: z.number(),
  p50_ms: z.number().nullable(),
  p95_ms: z.number().nullable(),
  errors: z.number(),
  client_errors: z.number(),
  sheds: z.number(),
  last_seen: z.string().nullable(),
})
export type QdrantClientRow = z.infer<typeof QdrantClientRow>

export const QdrantClients = z.object({
  window: z.string(),
  /* Literal client === 'overall' -- the same rows shape as `clients`, summed.
   * Rendered pinned first, distinguished, and never itself clickable: it is
   * a summary row, not a caller to filter down to. */
  overall: QdrantClientRow,
  clients: z.array(QdrantClientRow),
  ...qdrantStamped,
})
export type QdrantClients = z.infer<typeof QdrantClients>

/* ------------------------------------------------------------------ mapper keys
 * Contracts for collector/api/mapper_admin.py -- KEYS panel (spec §6/§4).
 * Session-cookie auth only (never x-api-key); field names mirror the server
 * exactly. The full plaintext key is carried ONLY by MapperKeyCreated, once,
 * on create/rotate -- MapperKeyRow (the list shape) never repeats it. */

export const MapperKeyEntity = z.object({
  id: z.string(), name: z.string(), hint: z.string(),
  status: z.enum(['active', 'revoked']),
  created_at: z.string(), revoked_at: z.string().nullable(),
  rotated_from: z.string().nullable(), note: z.string().nullable(),
})
export type MapperKeyEntity = z.infer<typeof MapperKeyEntity>

/** List rows only -- `last_seen` is joined from request_metrics server-side
 *  and is not part of the bare entity create/revoke/rotate return. */
export const MapperKeyRow = MapperKeyEntity.extend({ last_seen: z.string().nullable() })
export type MapperKeyRow = z.infer<typeof MapperKeyRow>

export const MapperKeysResponse = z.object({ keys: z.array(MapperKeyRow), computed_at: z.string() })
export type MapperKeysResponse = z.infer<typeof MapperKeysResponse>

/** create/rotate only -- the plaintext value, returned exactly once. */
export const MapperKeyCreated = MapperKeyEntity.extend({ key: z.string() })
export type MapperKeyCreated = z.infer<typeof MapperKeyCreated>

/* ------------------------------------------------------------------ TTD tab
 * The collector's own contract for /metrics/ttd/* (mirrors-collector repo,
 * collector/api/ttd_metrics.py) — served exactly like every other tab, no
 * dashboard-side proxy or backend of its own. That router reads the TTD
 * content pipeline's own Postgres (ttd_asset / ttd_iris_asset /
 * ttd_source_data) and ClickHouse (ctv_stats) DIRECTLY, the same databases
 * the colleague-owned ttd_backend_api repo reads for its own dashboards.
 * Field shapes were confirmed against the REAL tables
 * (information_schema.columns + live row counts) — see the Change
 * Contract. */

const TtdStageCount = z.object({ stage: z.string(), count: z.number() })

export const TtdAssetPipeline = z.object({
  counts: z.array(TtdStageCount), total_assets: z.number(),
  date_from: z.string(), date_to: z.string(),
})
export type TtdAssetPipeline = z.infer<typeof TtdAssetPipeline>

export const TtdIrisAssetPipeline = z.object({
  counts: z.array(TtdStageCount), date_from: z.string(), date_to: z.string(),
})
export type TtdIrisAssetPipeline = z.infer<typeof TtdIrisAssetPipeline>

const TtdSourceEntry = z.object({
  source: z.string(), total: z.number(), done: z.number(), pending: z.number(),
  identification_failed: z.number(), categorized: z.number(), not_categorized: z.number(),
})
export const TtdSourceData = z.object({
  sources: z.array(TtdSourceEntry), date_from: z.string(), date_to: z.string(),
})
export type TtdSourceData = z.infer<typeof TtdSourceData>
export type TtdSourceEntry = z.infer<typeof TtdSourceEntry>

export const TtdCtvDaily = z.object({
  days: z.array(z.object({
    date: z.string(), total: z.number(), success: z.number(), failure: z.number(),
  })),
  date_from: z.string(), date_to: z.string(),
})
export type TtdCtvDaily = z.infer<typeof TtdCtvDaily>

export const TtdCtvMatchedbyDaily = z.object({
  entries: z.array(z.object({ date: z.string(), matchedby: z.string(), total: z.number() })),
  date_from: z.string(), date_to: z.string(),
})
export type TtdCtvMatchedbyDaily = z.infer<typeof TtdCtvMatchedbyDaily>

export const TtdCtvMatchedbyBuckets = z.object({
  buckets: z.array(z.object({ label: z.string(), total: z.number() })),
  date_from: z.string(), date_to: z.string(),
})
export type TtdCtvMatchedbyBuckets = z.infer<typeof TtdCtvMatchedbyBuckets>

export const TtdCtvStats = z.object({
  date_from: z.string(), date_to: z.string(),
  summary: z.object({
    total_rows: z.number(), success_count: z.number(), failure_count: z.number(),
    success_rate: z.number(), unique_content_ids: z.number(),
    iris_count: z.number(), non_iris_count: z.number(),
  }),
  matchedby_breakdown: z.array(z.object({
    matchedby: z.string(), success_count: z.number(), failure_count: z.number(), total: z.number(),
  })),
  top_failing_urls: z.array(z.object({
    url: z.string(), failure_count: z.number(), success_count: z.number(), total: z.number(),
  })),
})
export type TtdCtvStats = z.infer<typeof TtdCtvStats>

/* ------------------------------------------------------------------ Scoring tab
 * The collector's contract for /metrics/scoring/* (mirrors-collector repo,
 * collector/api/scoring_metrics.py): the channel-scoring pipeline's feeder ->
 * queue -> calculators -> supplier flow plus its CSV-job side. Verified against
 * the backend's own golden payloads (panels/__fixtures__/scoring/*.json), which
 * are what the API actually serves.
 *
 * NULL IS NOT ZERO, end to end, and it is the whole point of this contract.
 * The server sends `null` for "cannot state": a Redis key never written, a
 * source it could not read, a rate with no baseline yet, a pipeline that has not
 * reported since the deploy. Every figure it can send as null is `.nullable()`
 * here, never `.default(0)` — a coerced 0 would paint a fresh deploy as a dead
 * pipeline (`alive: 0` raises `role_dead`) and an unreadable queue as an empty
 * one. Consumers render null through the board's "unstated" register.
 *
 * Unknown extra fields are dropped, not rejected (no `.strict()`): the backend
 * may add fields ahead of the board. A renamed or retyped field still fails
 * loudly at the boundary, as everywhere else in this file. */

/** `{id, sev, title, why, value}` — one thing the operator should know. The
 *  backend's single list of judgements: this tab, Triage, the rail and the
 *  alert email all render it. `id` is OPAQUE (`score_shift.10M+`,
 *  `role_dead.calculator`): never constrain it to a character class. `info`
 *  findings stay on the tab; only `crit`/`warn` reach Triage. */
export const ScoringFinding = z.object({
  id: z.string(),
  sev: z.enum(['crit', 'warn', 'info']),
  title: z.string(),
  why: z.string(),
  value: z.string(),
})
export type ScoringFinding = z.infer<typeof ScoringFinding>

const scoringFindings = { findings: z.array(ScoringFinding) }

export const ScoringRole = z.object({
  /** 'feeder' | 'calculator' | 'supplier' */
  role: z.string(),
  /** replicas the deployment is supposed to run (0 = scaled away on purpose) */
  expected: z.number(),
  /** live heartbeats. null = scoring has never reported, so a role that never
   *  beat cannot be told from one whose heartbeats all expired — NOT zero */
  alive: z.number().nullable(),
  instances: z.array(z.object({ instance: z.string(), beat_s: z.number().nullable() })),
})
export type ScoringRole = z.infer<typeof ScoringRole>

/** Same shape as `Throughput.roles` entries: null `per_s` is "measuring". */
export const ScoringThroughputEntry = z.object({
  total: z.number().nullable(),
  per_s: z.number().nullable(),
  per_min: z.number().nullable(),
})
export type ScoringThroughputEntry = z.infer<typeof ScoringThroughputEntry>

/** p50/p99 null with `samples: 0` = nothing measured yet. */
export const ScoringLatency = z.object({
  p50: z.number().nullable(),
  p99: z.number().nullable(),
  samples: z.number(),
})
export type ScoringLatency = z.infer<typeof ScoringLatency>

export const ScoringOverview = z.object({
  generated_at: z.string(),
  roles: z.array(ScoringRole),
  /** keyed `scoring-feeder` / `scoring-calculator` / `scoring-supplier` */
  throughput: z.record(z.string(), ScoringThroughputEntry),
  /** seconds since each stage last moved; null = never stamped */
  freshness: z.object({
    scored_s: z.number().nullable(),
    written_s: z.number().nullable(),
    fed_s: z.number().nullable(),
    /** age and `kind` of the newest entry of the failures ring (what
     *  `calculator_failing` reads); both null when the ring is empty or
     *  unreadable. `kind` is documented as 'channel' (one channel failed) |
     *  'batch' (a whole-batch failure) but the collector passes the ring's own
     *  string through — and its /failures `kinds` counter already allows
     *  others — so it is a string here, not an enum: an odd ring entry must
     *  not blank the whole overview over a field the board only carries. */
    failed_s: z.number().nullable(),
    failed_kind: z.string().nullable(),
  }),
  /** Mongo work queue. Every count is null (and `error` says why) when the
   *  queue could not be read; a readable status with no documents is a real 0. */
  queue: z.object({
    pending: z.number().nullable(),
    inprogress: z.number().nullable(),
    done: z.number().nullable(),
    failed: z.number().nullable(),
    /** seconds since `updated_at` of the oldest doc in each status: a claim's
     *  stamp (`inprogress`), the feeder's insert stamp (`pending` — how long
     *  queued work has waited) and the calculator's write stamp (`done` — how
     *  long scored work has waited for the supplier). null when no such doc
     *  carries a real date, which is not the same as "none waiting". */
    oldest_inprogress_s: z.number().nullable(),
    oldest_pending_s: z.number().nullable(),
    oldest_done_s: z.number().nullable(),
    error: z.string().nullable(),
  }),
  feeder: z.object({
    phase: z.string().nullable(),
    tier: z.string().nullable(),
    tier_batches: z.number().nullable(),
    last_batch: z.number().nullable(),
    last_query_ms: z.number().nullable(),
    queue_size: z.number().nullable(),
    updated_at: z.number().nullable(),
    updated_s: z.number().nullable(),
  }),
  latency: z.object({
    batch: ScoringLatency,
    fetch: ScoringLatency,
    feed_query: ScoringLatency,
  }),
  /** today's UTC-day counters; null before the day's first batch lands */
  today: z.object({
    scored: z.number().nullable(),
    failed: z.number().nullable(),
    reclaimed: z.number().nullable(),
  }),
  /** one probe of the scoring API's /health */
  api: z.object({
    ok: z.boolean().nullable(),
    status: z.number().nullable(),
    latency_ms: z.number().nullable(),
    error: z.string().nullable(),
  }),
  celery_backlog: z.number().nullable(),
  info: z.object({
    score_version: z.string().nullable(),
    rapidapi_enabled: z.boolean().nullable(),
    updated_at: z.number().nullable(),
  }),
  redis_error: z.string().nullable(),
  ...scoringFindings,
  /** worst severity among the findings; `idle` = scoring has never reported
   *  (a fresh deploy: not a fault, and not the same thing as healthy) */
  state: z.enum(['crit', 'warn', 'ok', 'idle']),
})
export type ScoringOverview = z.infer<typeof ScoringOverview>

/** Redis outcome hashes are open-ended on purpose — `n`, `score_sum`,
 *  `tier:{tier}:n`, `gate:{gate}`, `band:{band}`, `age:{bucket}`, `age_n`,
 *  `age_sum`, `capped:{kind}`, `version:{v}` and the per-tier variants — so a new
 *  tier, band or gate arrives as a new key, not a schema change. */
const ScoringOutcomeFields = z.record(z.string(), z.number())

export const ScoringOutcomes = z.object({
  generated_at: z.string(),
  window_days: z.number(),
  /** per UTC day for the window, newest first, no gaps (`fields` is `{}` for a
   *  day with nothing recorded) */
  days: z.array(z.object({ day: z.string(), fields: ScoringOutcomeFields })),
  totals: ScoringOutcomeFields,
  /** null when no day in the window recorded a data age */
  median_age_days: z.number().nullable(),
  ...scoringFindings,
})
export type ScoringOutcomes = z.infer<typeof ScoringOutcomes>

export const ScoringProgress = z.object({
  generated_at: z.string(),
  pending_index: z.string(),
  /** planner estimates (`pg_class.reltuples`), never a scan of channel_info */
  table_rows_est: z.number().nullable(),
  pending_est: z.number().nullable(),
  pct_scored_est: z.number().nullable(),
  /** true when pending_est was counted from the partial index, not estimated */
  pending_exact: z.boolean().optional(),
  /** when those statistics were last refreshed; they lag the truth */
  stats_as_of: z.string().nullable(),
  /** rows written per score_version. `{}` = readable, nothing written yet;
   *  null = could not be read */
  written: z.record(z.string(), z.number()).nullable(),
  feeder: z.object({ tier: z.string().nullable(), phase: z.string().nullable() }),
  /** null when no recent pace can be stated — "cannot state", not "done" */
  eta_s: z.number().nullable(),
  eta_rate_per_s: z.number().nullable(),
  /** which day's pace the ETA used: 'today' | 'yesterday' */
  eta_basis: z.string().nullable(),
  /** set when a source was unreadable (such a payload is served, never cached) */
  error: z.string().nullable(),
  cached: z.boolean(),
  cache_age_s: z.number(),
  cache_ttl_s: z.number(),
})
export type ScoringProgress = z.infer<typeof ScoringProgress>

const ScoringJob = z.object({
  job_id: z.string(),
  /** PENDING | PROCESSING | COMPLETED | FAILED */
  status: z.string(),
  input_type: z.string().nullable(),
  total: z.number().nullable(),
  processed: z.number().nullable(),
  /** null on schemas without the column */
  failed_channels: z.number().nullable(),
  /** the job's error text, truncated to 200 characters server-side */
  error_message: z.string().nullable(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  /** seconds since the last update (creation when never updated) */
  age_s: z.number().nullable(),
})
export type ScoringJob = z.infer<typeof ScoringJob>

export const ScoringJobs = z.object({
  generated_at: z.string(),
  limit: z.number(),
  jobs: z.array(ScoringJob),
  /** over the whole table, not just the rows shown; every status is always
   *  present (0 when absent) */
  counts: z.record(z.string(), z.number()),
  ...scoringFindings,
})
export type ScoringJobs = z.infer<typeof ScoringJobs>

const ScoringFailureItem = z.object({
  /** null when the failing entry named no channel */
  channelid: z.string().nullable(),
  error: z.string(),
  /** 'channel' (one channel failed) | 'batch' (a whole-batch outage) */
  kind: z.string(),
  at: z.number().nullable(),
  age_s: z.number().nullable(),
})
export type ScoringFailureItem = z.infer<typeof ScoringFailureItem>

export const ScoringFailures = z.object({
  generated_at: z.string(),
  /** entries parsed from the newest-100 ring */
  total: z.number(),
  /** ring entries that were not a JSON object */
  malformed: z.number(),
  kinds: z.record(z.string(), z.number()),
  /** newest first */
  items: z.array(ScoringFailureItem),
  by_error: z.array(z.object({ error: z.string(), count: z.number() })),
  /** channels failing repeatedly (per-channel failures only), worst first */
  repeated: z.array(z.object({
    channelid: z.string(),
    count: z.number(),
    last_at: z.number().nullable(),
  })),
  ...scoringFindings,
})
export type ScoringFailures = z.infer<typeof ScoringFailures>
